/**
 * Discover Startups Screen
 *
 * Browse and discover startups with featured, trending, and new sections.
 */

import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
} from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Stack, useRouter } from 'expo-router';
import Animated, { FadeIn, FadeInDown, FadeInUp } from 'react-native-reanimated';
import {
  ChevronLeft,
  TrendingUp,
  Sparkles,
  Clock,
  Star,
  MapPin,
  FolderOpen,
  ChevronRight,
  Scale,
  Bookmark,
  BookmarkCheck,
} from 'lucide-react-native';
import { getScoreColor } from '@/lib/colors';
import { useThemeColors } from '@/lib/useThemeColors';
import { track, useTrackScreen } from '@/lib/analytics';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { useResponsive } from '@/lib/useResponsive';
import { useProfileStore } from '@/lib/state/profile-store';
import type { StartupSummary } from '@/lib/supabase/edge-functions';
import { formatCurrency } from '@/lib/utils/format';
import {
  useDealflowSearch,
  useComparison,
  useSavedItems,
  STAGE_LABELS,
  calculateMatchScore,
} from '@/lib/state/marketplace';
import { useRaisingStartups } from '@/lib/supabase/investor-queries';
import { dataRoomOpen } from '@/lib/dealflow/stage';
import { SearchBar, FilterChips, FilterPanel, SortDropdown } from '@/components/SearchFilters';
import { SectionDivider } from '@/components/SectionDivider';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';

// Breadcrumbs for navigation
const BREADCRUMBS: Breadcrumb[] = [
  { label: 'Dealflow', href: '/(investor)/dealflow' },
  { label: 'Discover Startups' },
];

// ============================================
// Featured Startup Card
// ============================================

interface FeaturedCardProps {
  startup: StartupSummary;
  /** null when the startup has no score event — rendered as "Not scored". */
  score: number | null;
  matchScore?: number;
  onPress: () => void;
  isSaved: boolean;
  onToggleSave: () => void;
}

function FeaturedCard({ startup, score, matchScore, onPress, isSaved, onToggleSave }: FeaturedCardProps) {
  const colors = useThemeColors();
  return (
    <AnimatedPressable
      onPress={onPress}
      className="w-72 rounded-2xl overflow-hidden mr-4"
      style={{
        backgroundColor: colors.surface.primary,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 5,
      }}
    >
      {/* Featured Badge */}
      <View
        className="px-3 py-1.5 flex-row items-center gap-1"
        style={{ backgroundColor: colors.primary[600] }}
      >
        <Sparkles size={14} color="white" />
        <Text className="text-white text-xs font-medium">Featured</Text>
      </View>

      <View className="p-4">
        <View className="flex-row items-start justify-between mb-3">
          <View
            className="w-14 h-14 rounded-xl items-center justify-center"
            style={{ backgroundColor: colors.primary[100] }}
          >
            <Text style={{ color: colors.primary[700] }} className="text-lg font-bold">
              {startup.company.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </Text>
          </View>
          <Pressable onPress={onToggleSave} className="p-2">
            {isSaved ? (
              <BookmarkCheck size={22} color={colors.primary[600]} fill={colors.primary[600]} />
            ) : (
              <Bookmark size={22} color={colors.text.tertiary} />
            )}
          </Pressable>
        </View>

        <Text style={{ color: colors.text.primary }} className="font-semibold text-base">
          {startup.company.name}
        </Text>
        <Text style={{ color: colors.text.secondary }} className="text-sm mt-1" numberOfLines={2}>
          {startup.company.tagline}
        </Text>

        <View className="flex-row items-center gap-2 mt-3">
          <View className="px-2 py-1 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
            <Text style={{ color: colors.text.secondary }} className="text-xs">{startup.company.industry}</Text>
          </View>
          <View className="px-2 py-1 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
            <Text style={{ color: colors.text.secondary }} className="text-xs">
              {STAGE_LABELS[startup.company.stage]}
            </Text>
          </View>
        </View>

        <View className="flex-row items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: colors.border.light }}>
          <View className="flex-row items-center gap-1">
            <TrendingUp size={16} color={score != null ? getScoreColor(score) : colors.text.tertiary} />
            <Text
              style={{ color: score != null ? getScoreColor(score) : colors.text.tertiary }}
              className="font-bold"
            >
              {score ?? 'Not scored'}
            </Text>
          </View>
          {matchScore && (
            <View className="flex-row items-center gap-1 px-2 py-1 rounded-full" style={{ backgroundColor: colors.traffic.greenLight }}>
              <Star size={12} color={colors.success} />
              <Text style={{ color: colors.success }} className="text-xs font-medium">{matchScore}% match</Text>
            </View>
          )}
        </View>
      </View>
    </AnimatedPressable>
  );
}

// ============================================
// Startup List Card
// ============================================

interface StartupCardProps {
  startup: StartupSummary;
  /** null when the startup has no score event — rendered as "Not scored". */
  score: number | null;
  matchScore?: number;
  hasDataRoom: boolean;
  onPress: () => void;
  isInComparison: boolean;
  onToggleCompare: () => void;
  isSaved: boolean;
  onToggleSave: () => void;
}

function StartupCard({
  startup,
  score,
  matchScore,
  hasDataRoom,
  onPress,
  isInComparison,
  onToggleCompare,
  isSaved,
  onToggleSave,
}: StartupCardProps) {
  const colors = useThemeColors();
  return (
    <AnimatedPressable
      onPress={onPress}
      className="rounded-2xl overflow-hidden mb-3"
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: isInComparison ? 2 : 0,
        borderColor: isInComparison ? colors.primary[400] : 'transparent',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 3,
      }}
    >
      <View className="p-4">
        <View className="flex-row items-start gap-3">
          <View
            className="w-12 h-12 rounded-xl items-center justify-center"
            style={{ backgroundColor: colors.primary[100] }}
          >
            <Text style={{ color: colors.primary[700] }} className="font-bold">
              {startup.company.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </Text>
          </View>

          <View className="flex-1">
            <View className="flex-row items-center justify-between">
              <Text style={{ color: colors.text.primary }} className="font-semibold">{startup.company.name}</Text>
              <View className="flex-row items-center gap-1">
                <TrendingUp size={14} color={score != null ? getScoreColor(score) : colors.text.tertiary} />
                <Text
                  style={{ color: score != null ? getScoreColor(score) : colors.text.tertiary }}
                  className="font-bold"
                >
                  {score ?? 'Not scored'}
                </Text>
              </View>
            </View>
            <Text style={{ color: colors.text.secondary }} className="text-sm mt-0.5" numberOfLines={1}>
              {startup.company.tagline}
            </Text>

            <View className="flex-row items-center gap-2 mt-2 flex-wrap">
              <View className="flex-row items-center gap-1">
                <MapPin size={12} color={colors.text.tertiary} />
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  {startup.company.location.split(',')[0]}
                </Text>
              </View>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">•</Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">{startup.company.industry}</Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">•</Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">{STAGE_LABELS[startup.company.stage]}</Text>
              {hasDataRoom && (
                <>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">•</Text>
                  <View className="flex-row items-center gap-1">
                    <FolderOpen size={12} color={colors.success} />
                    <Text style={{ color: colors.success }} className="text-xs">Data Room</Text>
                  </View>
                </>
              )}
            </View>
          </View>
        </View>

        {/* Actions */}
        <View className="flex-row items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: colors.border.light }}>
          <View className="flex-row items-center gap-2">
            {matchScore && (
              <View className="flex-row items-center gap-1 px-2 py-1 rounded-full" style={{ backgroundColor: colors.traffic.greenLight }}>
                <Star size={12} color={colors.success} />
                <Text style={{ color: colors.success }} className="text-xs font-medium">{matchScore}% match</Text>
              </View>
            )}
            <Text style={{ color: colors.text.secondary }} className="text-sm">
              {formatCurrency(startup.funding.currentRaise?.amount ?? 0)} target
            </Text>
          </View>

          <View className="flex-row items-center gap-2">
            <Pressable
              onPress={(e) => {
                e.stopPropagation();
                onToggleSave();
              }}
              className="p-2"
            >
              {isSaved ? (
                <BookmarkCheck size={20} color={colors.primary[600]} fill={colors.primary[600]} />
              ) : (
                <Bookmark size={20} color={colors.text.tertiary} />
              )}
            </Pressable>
            <Pressable
              onPress={(e) => {
                e.stopPropagation();
                onToggleCompare();
              }}
              className="p-2"
            >
              <Scale size={20} color={isInComparison ? colors.primary[600] : colors.text.tertiary} />
            </Pressable>
            <ChevronRight size={20} color={colors.text.tertiary} />
          </View>
        </View>
      </View>
    </AnimatedPressable>
  );
}

// ============================================
// Main Screen
// ============================================

export default function DiscoverStartupsScreen() {
  useTrackScreen('DiscoverStartups');
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const { isDesktop } = useResponsive();
  const [showFilters, setShowFilters] = useState(false);

  const investorProfile = useProfileStore((s) => s.investorProfile);

  // Actively-raising startups feed (production; hook is disabled in sample
  // mode) — Discover shows the whole platform, not just the own pipeline.
  const { data: raisingFeed = [] } = useRaisingStartups();

  // Search and filter hook (pipeline + raising feed, deduped)
  const {
    filters,
    updateFilter,
    toggleArrayFilter,
    resetFilters,
    itemsWithMatch,
    sortBy,
    setSortBy,
    activeFilterCount,
  } = useDealflowSearch(raisingFeed);

  // Comparison hook
  const {
    comparisonIds,
    comparisonCount,
    addToComparison,
    removeFromComparison,
    isInComparison,
    clearComparison,
    canAddMore,
  } = useComparison(4);

  // Saved items hook
  const { isItemSaved, saveItem, unsaveItem } = useSavedItems();

  // Featured startups (top matches)
  const featuredStartups = useMemo(() => {
    return itemsWithMatch
      .filter(item => item.matchScore && item.matchScore.overall >= 70)
      .slice(0, 5);
  }, [itemsWithMatch]);

  // Navigate to comparison
  const handleCompare = () => {
    if (comparisonIds.length >= 2) {
      router.push(`/deal-comparison?ids=${comparisonIds.join(',')}`);
    }
  };

  const content = (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
        {/* Header */}
        <Animated.View
          entering={FadeInDown.duration(400)}
          className="px-5 py-4"
          style={{ backgroundColor: colors.surface.primary }}
        >
          <View className="flex-row items-center justify-between mb-4">
            {phoneShell && (
              <View className="flex-row items-center gap-3">
                <Pressable
                  onPress={() => router.back()}
                  className="w-10 h-10 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <ChevronLeft size={24} color={colors.text.primary} />
                </Pressable>
                <View>
                  <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
                    Discover Startups
                  </Text>
                  <Text style={{ color: colors.text.secondary }} className="text-sm">
                    {itemsWithMatch.length} opportunities
                  </Text>
                </View>
              </View>
            )}
            {isWeb && (
              <Text style={{ color: colors.text.secondary }} className="text-sm">
                {itemsWithMatch.length} opportunities
              </Text>
            )}

            <SortDropdown value={sortBy} onChange={setSortBy} />
          </View>

          <SearchBar
            value={filters.query}
            onChangeText={(text) => updateFilter('query', text)}
            placeholder="Search startups..."
            onFilterPress={() => setShowFilters(true)}
              activeFilterCount={activeFilterCount}
            />
          </Animated.View>

          {/* Filter Chips */}
          <View className="px-5 pt-3">
            <FilterChips
              filters={filters}
              onToggle={toggleArrayFilter}
              onReset={resetFilters}
            />
          </View>

          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {/* Section Divider - Top Matches */}
            {featuredStartups.length > 0 && !filters.query && (
              <View className="px-5">
                <SectionDivider label="Top Matches" marginTop={16} marginBottom={8} />
              </View>
            )}

            {/* Featured Section */}
            {featuredStartups.length > 0 && !filters.query && (
              <Animated.View entering={FadeInUp.delay(100).duration(400)}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ paddingLeft: 20, paddingRight: 4 }}
                  style={{ flexGrow: 0 }}
                >
                  {featuredStartups.map(({ deal, matchScore }) => (
                    <FeaturedCard
                      key={deal.id}
                      startup={deal.startup}
                      score={deal.readinessScore}
                      matchScore={matchScore?.overall}
                      onPress={() => router.push(`/startup-detail?id=${deal.id}`)}
                      isSaved={isItemSaved(deal.id)}
                      onToggleSave={() => {
                        if (isItemSaved(deal.id)) {
                          unsaveItem(deal.id);
                        } else {
                          saveItem('startup', deal.id);
                        }
                      }}
                    />
                  ))}
                </ScrollView>
              </Animated.View>
            )}

            {/* Section Divider - All Startups */}
            <View className="px-5">
              <SectionDivider label={filters.query ? 'Search Results' : 'All Startups'} marginTop={16} marginBottom={8} />
            </View>

            {/* All Startups */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(400)}
              className="px-5 py-4"
            >
              <View className="flex-row items-center justify-between mb-4">
                <Text style={{ color: colors.text.primary }} className="font-semibold">
                  {itemsWithMatch.length} opportunities
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-sm">
                  {itemsWithMatch.length} results
                </Text>
              </View>

              {itemsWithMatch.map(({ deal, matchScore }, index) => (
                <Animated.View key={deal.id} entering={FadeIn.delay(100 + index * 30).duration(200)}>
                  <StartupCard
                    startup={deal.startup}
                    score={deal.readinessScore}
                    matchScore={matchScore?.overall}
                    hasDataRoom={dataRoomOpen(deal.status)}
                    onPress={() => { track('startup_discovered', { startupId: deal.id }); router.push(`/startup-detail?id=${deal.id}`); }}
                    isInComparison={isInComparison(deal.id)}
                    onToggleCompare={() => {
                      if (isInComparison(deal.id)) {
                        removeFromComparison(deal.id);
                      } else if (canAddMore) {
                        addToComparison(deal.id);
                      }
                    }}
                    isSaved={isItemSaved(deal.id)}
                    onToggleSave={() => {
                      if (isItemSaved(deal.id)) {
                        unsaveItem(deal.id);
                      } else {
                        saveItem('startup', deal.id);
                      }
                    }}
                  />
                </Animated.View>
              ))}

              {itemsWithMatch.length === 0 && (
                <View className="items-center py-12">
                  <Text style={{ color: colors.text.tertiary }} className="text-center">
                    No startups match your criteria.{'\n'}Try adjusting your filters.
                  </Text>
                </View>
              )}
            </Animated.View>

            <View className="h-24" />
          </ScrollView>

          {/* Comparison Bar */}
          {comparisonCount > 0 && (
            <Animated.View
              entering={FadeInUp.duration(300)}
              className="absolute bottom-0 left-0 right-0 px-5 py-4"
              style={{
                backgroundColor: colors.surface.primary,
                borderTopWidth: 1,
                borderTopColor: colors.border.light,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: -4 },
                shadowOpacity: 0.1,
                shadowRadius: 12,
              }}
            >
              <View className="flex-row items-center justify-between">
                <View>
                  <Text style={{ color: colors.text.primary }} className="font-medium">
                    {comparisonCount} startup{comparisonCount > 1 ? 's' : ''} selected
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                    Select 2-4 to compare
                  </Text>
                </View>
                <View className="flex-row items-center gap-3">
                  <Pressable onPress={clearComparison} className="px-4 py-2">
                    <Text style={{ color: colors.text.secondary }} className="font-medium">Clear</Text>
                  </Pressable>
                  <Pressable
                    onPress={handleCompare}
                    disabled={comparisonCount < 2}
                    className="px-5 py-2.5 rounded-xl flex-row items-center gap-2"
                    style={{
                      backgroundColor: comparisonCount >= 2 ? colors.primary[600] : colors.light[300],
                    }}
                  >
                    <Scale size={18} color={comparisonCount >= 2 ? 'white' : colors.text.tertiary} />
                    <Text
                      className="font-medium"
                      style={{ color: comparisonCount >= 2 ? 'white' : colors.text.tertiary }}
                    >
                      Compare
                    </Text>
                  </Pressable>
                </View>
              </View>
            </Animated.View>
          )}
        </SafeAreaView>
      </View>
  );

  const filterPanel = (
    <FilterPanel
      visible={showFilters}
      onClose={() => setShowFilters(false)}
      filters={filters}
      onUpdateFilter={updateFilter}
      onToggleFilter={toggleArrayFilter}
      onReset={resetFilters}
      showStatus={false}
    />
  );

  // Wrap with AppLayout for web
  if (isWeb) {
    return (
      <AppLayout title="Discover Startups" breadcrumbs={BREADCRUMBS}>
        {content}
        {filterPanel}
      </AppLayout>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      {content}
      {filterPanel}
    </>
  );
}
